import { useLocalStorageState } from "@outschool/local-storage";

export type SelfElectedStateFundingProgram = {
  stateAbbreviation: string;
  stateName: string;
  fundingOptions: {
    statePortal: boolean;
    reimbursement: boolean;
    charter: boolean;
    selfPay: boolean;
  };
};

type SelfElectedStateFundingProgramBanner = SelfElectedStateFundingProgram & {
  searchFiltersActive: boolean;
  hideBanner: boolean;
};

// example self elected state funding program
// {
//     searchFiltersActive: true,
//     stateAbbreviation: "AR",
//     stateName: "Arkansas",
//     fundingOptions: {
//         statePortal: true,
//         reimbursement: false,
//         charter: false,
//         selfPay: true
//     }
// }
// stringified:
// {"searchFiltersActive":true,"stateAbbreviation":"AR","stateName": "Arkansas","fundingOptions":{"statePortal":true,"reimbursement":false,"charter":false,"selfPay":true}}
// key: self-elected-state-funding-program

/**
 * This hook is used to get and set the self-elected state funding program info for a logged out user
 * The information is stored in local storage and the info once set can (if the funding is state not charter) affect the search filters applied
 * @returns
 */
export function useSelfElectedStateFundingProgram() {
  const [selfElectedStateFundingProgram, setSelfElectedStateFundingProgram] =
    useLocalStorageState<SelfElectedStateFundingProgramBanner>(
      "self-elected-state-funding-program",
      {
        searchFiltersActive: false,
        stateAbbreviation: "",
        stateName: "",
        fundingOptions: {
          statePortal: false,
          reimbursement: false,
          charter: false,
          selfPay: false,
        },
        hideBanner: false,
      }
    );

  const activateSearchFilters = () => {
    setSelfElectedStateFundingProgram({
      ...selfElectedStateFundingProgram,
      searchFiltersActive: true,
    });
  };

  const deactivateSearchFilters = () => {
    setSelfElectedStateFundingProgram({
      ...selfElectedStateFundingProgram,
      searchFiltersActive: false,
    });
  };

  const hideBanner = () => {
    setSelfElectedStateFundingProgram({
      ...selfElectedStateFundingProgram,
      hideBanner: true,
    });
  };

  const setState = (
    stateAbbreviation: string,
    stateName: string,
    activateSearchFilters: boolean = true
  ) => {
    setSelfElectedStateFundingProgram({
      ...selfElectedStateFundingProgram,
      stateAbbreviation,
      stateName,
      searchFiltersActive: activateSearchFilters,
    });
  };

  const setFundingOptions = (
    key: keyof SelfElectedStateFundingProgramBanner["fundingOptions"],
    value: boolean
  ) => {
    const updatedSelfElectedStateFundingProgram = {
      ...selfElectedStateFundingProgram,
      fundingOptions: {
        ...selfElectedStateFundingProgram.fundingOptions,
        [key]: value,
      },
    };

    if (updatedSelfElectedStateFundingProgram.fundingOptions.statePortal) {
      updatedSelfElectedStateFundingProgram.searchFiltersActive = true;
    }

    setSelfElectedStateFundingProgram(updatedSelfElectedStateFundingProgram);
  };

  const usesPublicFundingOrCharter = () => {
    return Boolean(selfElectedStateFundingProgram.fundingOptions.charter);
  };

  const createAccountVariables = () => {
    return {
      stateAbbreviation: selfElectedStateFundingProgram.stateAbbreviation,
      stateName: selfElectedStateFundingProgram.stateName,
      fundingOptions: selfElectedStateFundingProgram.fundingOptions,
    };
  };

  return {
    selfElectedStateFundingProgram,
    setSelfElectedStateFundingProgram,
    activateSearchFilters,
    deactivateSearchFilters,
    setFundingOptions,
    hideBanner,
    setState,
    usesPublicFundingOrCharter,
    createAccountVariables,
  };
}
